import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VehicleCard } from '@/components/shared/VehicleCard';
import type { FipeVehicle, NeverTestedFipeVehicle, PbevVehicle } from '@/types';
import {
  filterVehicles,
  formatPbevVehicleLabel,
  formatPbevVehicleSpecs,
} from '@/utils/helpers';
import { Search } from 'lucide-react';

interface MatchingInterfaceProps {
  fipeVehicles: FipeVehicle[];
  neverTestedVehicles: NeverTestedFipeVehicle[];
  pbevVehicles: PbevVehicle[];
  suggestions: PbevVehicle[];
  selectedFipe: FipeVehicle | null;
  onSelectFipe: (vehicle: FipeVehicle) => void;
  onCreateMatch: (pbevId: number) => void;
  onMarkNeverTested: (vehicle: FipeVehicle) => void;
  onRestoreNeverTested: (modelId: number) => void;
  loading: boolean;
}

export function MatchingInterface({
  fipeVehicles,
  neverTestedVehicles,
  pbevVehicles,
  suggestions,
  selectedFipe,
  onSelectFipe,
  onCreateMatch,
  onMarkNeverTested,
  onRestoreNeverTested,
  loading,
}: MatchingInterfaceProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [fipeQueueView, setFipeQueueView] = useState('matchable');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Unmatched FIPE List */}
      <Card>
        <Tabs value={fipeQueueView} onValueChange={setFipeQueueView}>
          <CardHeader className="space-y-4">
            <CardTitle>FIPE Review Queue</CardTitle>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="matchable">Matchable ({fipeVehicles.length})</TabsTrigger>
              <TabsTrigger value="never-tested">Never Tested ({neverTestedVehicles.length})</TabsTrigger>
            </TabsList>
          </CardHeader>
          <CardContent className="max-h-[600px] overflow-y-auto">
            <TabsContent value="matchable" className="mt-0 space-y-2">
              {fipeVehicles.map((fipe) => (
                <VehicleCard
                  key={fipe.model_id}
                  vehicle={fipe}
                  selected={selectedFipe?.model_id === fipe.model_id}
                  onClick={() => onSelectFipe(fipe)}
                />
              ))}
            </TabsContent>

            <TabsContent value="never-tested" className="mt-0 space-y-3">
              {neverTestedVehicles.map((vehicle) => (
                <div key={vehicle.model_id} className="space-y-2 rounded-lg border p-3">
                  <VehicleCard vehicle={vehicle} />
                  <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                    <span>Marked: {new Date(vehicle.marked_at).toLocaleDateString()}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onRestoreNeverTested(vehicle.model_id)}
                      disabled={loading}
                    >
                      Restore
                    </Button>
                  </div>
                </div>
              ))}
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      {/* Matching Panel */}
      <Card>
        <CardHeader>
          <CardTitle>Match Selection</CardTitle>
        </CardHeader>
        <CardContent>
          {selectedFipe ? (
            <div className="space-y-4">
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h3 className="font-medium">Selected FIPE Vehicle</h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onMarkNeverTested(selectedFipe)}
                    disabled={loading}
                  >
                    Mark as never tested
                  </Button>
                </div>
                <VehicleCard vehicle={selectedFipe} />
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by modelo, versao, fuel, transmission, year..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div>
                <h3 className="font-medium mb-2">Suggested Matches</h3>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {(searchTerm
                    ? filterVehicles(pbevVehicles, searchTerm)
                    : suggestions
                  )
                    .slice(0, 10)
                    .map((suggestion) => {
                      const suggestionSpecs = formatPbevVehicleSpecs(suggestion);

                      return (
                        <div
                          key={suggestion.pbev_id}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="font-medium text-sm">
                              {formatPbevVehicleLabel(suggestion)}
                            </div>
                            {suggestionSpecs && (
                              <div className="text-xs text-muted-foreground">
                                {suggestionSpecs}
                              </div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => onCreateMatch(suggestion.pbev_id)}
                            disabled={loading}
                          >
                            Match
                          </Button>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[400px] text-muted-foreground">
              Select a FIPE vehicle to start matching
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unmatched PBEV List */}
      <Card>
        <CardHeader>
          <CardTitle>Unmatched PBEV ({pbevVehicles.length})</CardTitle>
        </CardHeader>
        <CardContent className="max-h-[600px] overflow-y-auto space-y-2">
          {filterVehicles(pbevVehicles, searchTerm)
            .slice(0, 20)
            .map((pbev) => {
              const pbevSpecs = formatPbevVehicleSpecs(pbev);

              return (
                <div key={pbev.pbev_id} className="p-3 border rounded-lg">
                  <div className="font-medium text-sm">{formatPbevVehicleLabel(pbev)}</div>
                  {pbevSpecs && (
                    <div className="text-xs text-muted-foreground">{pbevSpecs}</div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    ID: {pbev.pbev_id}
                  </div>
                </div>
              );
            })}
        </CardContent>
      </Card>
    </div>
  );
}
